import { ProblemData } from "../../MathFactsGenerator/Constants.ts";
import { checkAnswer } from "../Quiz/answer.ts";
import { PictureId } from "./pictureArt.ts";

/*
 * Hidden Picture's rules (ADR 0133), pure: no React, no DOM, no timers, and no
 * randomness of its own. A board is 25 tiles over one picture; a right answer
 * lifts a tile, and a second miss shows the answer and lifts it anyway, so the
 * picture always finishes. Facts come in through a `next()` function (the quiz's
 * queue), called by the page when Start is pressed and never while rendering.
 */

export const GRID = 5;
export const TILE_COUNT = GRID * GRID;
/** Each tile hides a 4 x 4 block of the 20 x 20 picture. */
export const TILE_PIXELS = 4;

/** Draws allowed per tile to find a fact not yet on the board (see `buildBoard`). */
const MAX_REDRAWS = 40;

export type Misses = 0 | 1 | 2;

export interface Tile {
    problem: ProblemData;
    /** Wrong tries on this tile. A tile keeps its misses until it lifts (ADR 0133 §1). */
    misses: Misses;
    lifted: boolean;
}

export interface PictureState {
    tiles: Tile[];
    /** The tile being answered; null once every tile has lifted. */
    selected: number | null;
}

/** What one press of Check did. `missed` is the second wrong try: the tile lifted anyway. */
export type PictureOutcome = "skipped" | "correct" | "wrong" | "missed";

/* ── The calendar (§2) ─────────────────────────────────────────────────── */

/** The day of November (1-30) that is the fourth Thursday of `year`. */
export function thanksgivingDay(year: number): number {
    const firstOfNovember = new Date(year, 10, 1).getDay();
    const firstThursday = 1 + ((4 - firstOfNovember + 7) % 7);
    return firstThursday + 21;
}

/**
 * The picture for a day, by the date's local month and day. Each window runs up
 * to its holiday, so the picture is the one a class is getting ready for. The
 * turkey stays through Thanksgiving day itself and the gift starts the day after.
 */
export function pictureFor(date: Date): PictureId {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    switch (month) {
        case 1:
            return "snowman";
        case 2:
            return day <= 14 ? "heart" : "shamrock";
        case 3:
            return day <= 17 ? "shamrock" : "tulip";
        case 4:
        case 5:
            return "tulip";
        case 6:
        case 7:
        case 8:
            return "icecream";
        case 9:
            return "apple";
        case 10:
            return "pumpkin";
        case 11:
            return day <= thanksgivingDay(date.getFullYear()) ? "turkey" : "gift";
        default:
            return "gift";
    }
}

/* ── Building a board ──────────────────────────────────────────────────── */

/** Same operator and operands: the thing a board avoids repeating. */
export function factId(problem: ProblemData): string {
    return `${problem.operator}|${problem.numbers[0]}|${problem.numbers[1]}`;
}

/**
 * 25 covered tiles. A fact is not repeated on one board while the range has 25
 * different ones: each tile redraws up to MAX_REDRAWS times for a new fact. A
 * range with fewer is found out by the first tile that runs out of redraws, and
 * from then on repeats are taken as they come, so the board still fills and the
 * work is bounded.
 */
export function buildBoard(next: () => ProblemData): Tile[] {
    const seen = new Set<string>();
    const tiles: Tile[] = [];
    let exhausted = false;
    while (tiles.length < TILE_COUNT) {
        let problem = next();
        if (!exhausted) {
            let tries = 1;
            while (seen.has(factId(problem)) && tries < MAX_REDRAWS) {
                problem = next();
                tries++;
            }
            if (seen.has(factId(problem))) {
                exhausted = true;
            }
        }
        seen.add(factId(problem));
        tiles.push({ problem, misses: 0, lifted: false });
    }
    return tiles;
}

/** A new game on a built board, with the first tile picked (§1). */
export function startBoard(tiles: Tile[]): PictureState {
    return { tiles, selected: tiles.length > 0 ? 0 : null };
}

/* ── Playing ───────────────────────────────────────────────────────────── */

/**
 * The next covered tile after `from` in reading order, wrapping past the end;
 * null when none is covered. `from` itself is the last one tried, so a lone
 * covered tile is its own successor.
 */
export function nextCovered(tiles: Tile[], from: number): number | null {
    for (let step = 1; step <= tiles.length; step++) {
        const index = (from + step) % tiles.length;
        if (!tiles[index].lifted) {
            return index;
        }
    }
    return null;
}

/** Picks a tile to answer. Only a covered tile can be picked; anything else changes nothing. */
export function selectTile(state: PictureState, index: number): PictureState {
    const tile = state.tiles[index];
    if (!tile || tile.lifted || index === state.selected) {
        return state;
    }
    return { ...state, selected: index };
}

/**
 * Marks a typed answer against the picked tile.
 *
 * - Empty: nothing happens, and the same state comes back.
 * - Right: the tile lifts and the next covered tile is picked.
 * - Wrong the first time: the tile stays, with one miss.
 * - Wrong the second time: the tile lifts anyway, with two misses, and moves on.
 */
export function answerSelected(state: PictureState, typed: string): { state: PictureState; outcome: PictureOutcome } {
    const index = state.selected;
    if (index === null) {
        return { state, outcome: "skipped" };
    }
    const tile = state.tiles[index];
    const result = checkAnswer(tile.problem, typed);
    if (result === "skipped") {
        return { state, outcome: "skipped" };
    }

    const lifts = result === "correct" || tile.misses === 1;
    const misses: Misses = result === "correct" ? tile.misses : tile.misses === 0 ? 1 : 2;
    const tiles = state.tiles.map((t, i) => (i === index ? { ...t, misses, lifted: lifts } : t));
    const selected = lifts ? nextCovered(tiles, index) : index;
    return {
        state: { tiles, selected },
        outcome: result === "correct" ? "correct" : lifts ? "missed" : "wrong",
    };
}

export function tilesLeft(state: PictureState): number {
    return state.tiles.filter((t) => !t.lifted).length;
}

export function isDone(state: PictureState): boolean {
    return state.tiles.every((t) => t.lifted);
}

/* ── Scoring (§1) ──────────────────────────────────────────────────────── */

/** Tiles lifted without a miss: the score, out of 25. */
export function firstTryCount(tiles: Tile[]): number {
    return tiles.filter((t) => t.lifted && t.misses === 0).length;
}

/** Wrong tries over the whole board, the second tries included. */
export function wrongTries(tiles: Tile[]): number {
    return tiles.reduce((sum, t) => sum + t.misses, 0);
}

/** Facts that lifted only after a second miss, in reading order: the ones to practise. */
export function missedFacts(tiles: Tile[]): ProblemData[] {
    return tiles.filter((t) => t.lifted && t.misses === 2).map((t) => t.problem);
}
